import React, { useCallback } from 'react';
import { UserPickerProps } from 'src/common/UserPicker';
import { confirm, notification, UserPicker } from 'src/common';
import { useTranslation } from 'react-i18next';
import modalStyles from 'src/common/Modal/index.module.scss';
import { useUpdateCompanyAdmin, useAdminCompany } from 'src/models/admin/CompanyUsers/hooks';
import { trackUpdateCompanyAdmin } from 'src/segment/events';

interface IProps extends Omit<UserPickerProps<'single'>, 'onSelect' | 'users' | 'label' | 'userPickerType'> {
  companyId: string;
  companyName: string;
}

export function UsersSelect({ size = 's', mode = 'full', ...restProps }: IProps): JSX.Element {
  const { companyId, companyName } = restProps;
  const { t } = useTranslation();

  const { loading: employeesLoading, data: company } = useAdminCompany(companyId);
  const users = company?.users || [];
  const [setNewAdmin, { loading }] = useUpdateCompanyAdmin();

  const onUpdateAdmin = useCallback(
    (email: string) => {
      confirm.confirm({
        title: t('AdminPage.updateAdminConfirmText'),
        content: t('AdminPage.updateAdminConfirmDescription'),
        okText: t('Common.yes'),
        cancelText: t('Common.no'),
        className: modalStyles.confirmModal,
        centered: true,
        onOk() {
          setNewAdmin({ variables: { email, companyId } })
            .then(() => {
              trackUpdateCompanyAdmin({
                email,
                companyId,
                companyName
              });
            })
            .catch(() => {
              notification.error({
                message: t('Teammates.employeeHasNotAdded'),
                description: t('Common.unknownErrorDesc')
              });
            });
        }
      });
    },
    [companyId, companyName, setNewAdmin, t]
  );

  return (
    <UserPicker
      isUpdating={loading}
      usersLoading={employeesLoading}
      size={size}
      mode={mode}
      users={users}
      label={t('Common.unassigned')}
      onSelect={onUpdateAdmin}
      userPickerType="single"
      {...restProps}
    />
  );
}

export default UsersSelect;
